<template>
  <portal
    to="root"
    :disabled="!teleport"
  >
    <div
      v-if="isOpen"
      :class="{ 'slide-over-visible': showSlideOver }"
      :data-testid="id"
      class="fixed inset-0 z-999 flex items-center justify-center"
      @click="handleOverlayClick"
      @keydown="handleOverlayClick"
    >
      <transition name="fade">
        <div
          v-if="showSlideOver && showOverlay"
          class="fixed inset-0 z-999 w-full h-full bg-black/75"
        />
      </transition>
      <transition
        name="slide-up"
        @after-enter="$emit('after-enter')"
        @after-leave="handleAfterLeave"
      >
        <div
          v-if="showSlideOver"
          ref="modal"
          :class="slideOverClasses"
          class="fixed z-999 w-full bg-white"
          @touchstart="touchStart"
          @touchend="touchEnd"
        >
          <focus-trap :active="shouldActivateFocusTrap">
            <div
              :class="{
                'rounded-t-lg': isSlidein,
                'overflow-y-auto': !overflowVisible,
              }"
              class="flex flex-col h-full w-full lg:rounded-lg"
            >
              <div
                v-if="showHeader"
                class="lg:rounded-lg lg:overflow-hidden"
              >
                <slot
                  name="header"
                  :close="close"
                />
              </div>

              <div
                :class="{ 'overflow-y-auto': !overflowVisible }"
                class="flex flex-1 flex-col"
              >
                <slot name="content" />
              </div>

              <div
                v-if="showFooter"
                class="shrink-0"
              >
                <slot name="footer" />
              </div>
            </div>
          </focus-trap>
        </div>
      </transition>
    </div>
  </portal>
</template>

<script>
import ClickAway from '@/mixins/ClickAway';
import CloseOnEscape from '@/mixins/CloseOnEscape';

export default {
  name: 'VetsterSlideOver',

  mixins: [
    ClickAway,
    CloseOnEscape,
  ],

  props: {
    confirmClose: {
      type: Boolean,
      default: false,
    },
    disableClickOutside: {
      type: Boolean,
      default: false,
    },
    disableCloseOnEscape: {
      type: Boolean,
      default: false,
    },
    containerClasses: {
      type: String,
      default: 'lg:h-3/4',
    },
    focusTrap: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      default: undefined,
    },
    isSlidein: {
      type: Boolean,
      default: false,
    },
    overflowVisible: {
      type: Boolean,
      default: false,
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'md',
    },
    teleport: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      endY: 0,
      startY: 0,
      isOpen: false,
      showSlideOver: false,
    };
  },

  computed: {
    shouldActivateFocusTrap() {
      return this.focusTrap && this.isOpen;
    },

    slideOverClasses() {
      return {
        'max-h-screen overflow-y-auto bottom-0 left-0 lg:bottom-auto lg:left-auto rounded-t-lg lg:rounded-b-lg': this.isSlidein,
        'h-full': !this.isSlidein,
        [this.containerClasses]: this.containerClasses,
        'lg:rounded-lg lg:max-h-[1000px]': !this.isSlidein && this.size !== 'full',
        'min-h-screen rounded-none': !this.isSlidein && this.size === 'full',
        'lg:max-w-md': this.size === 'xs',
        'lg:max-w-lg': this.size === 'sm',
        'lg:max-w-xl': this.size === 'md',
        'lg:max-w-2xl': this.size === 'lg',
        'lg:max-w-3xl': this.size === 'xl',
        'lg:max-w-4xl': this.size === '2xl',
        'lg:max-w-5xl': this.size === '3xl',
      };
    },

    showHeader() {
      return !!this.$slots.header;
    },

    showFooter() {
      return !!this.$slots.footer;
    },
  },

  watch: {
    disableCloseOnEscape(newValue) {
      if (newValue) {
        this.destroyCloseOnEscape();
      } else {
        this.closeOnEscape(this.close);
      }
    },
    isOpen(newValue) {
      if (newValue && !this.disableCloseOnEscape) {
        this.closeOnEscape(this.close);
      } else {
        this.destroyCloseOnEscape();
      }
    },
  },

  beforeDestroy() {
    this.destroyCloseOnEscape();
  },

  methods: {
    async close() {
      if (this.confirmClose) {
        if (await this.$confirmDialog(this.$t('app.are-you-sure-you-want-to-leave'))) {
          this.forceClose();
        }
      } else {
        this.forceClose();
      }
    },
    forceClose() {
      this.$bus.$emit('enable-scroll');
      this.$emit('close');
      this.showSlideOver = false;
    },
    handleAfterLeave() {
      this.isOpen = false;
    },
    handleOverlayClick(event) {
      if (!this.disableClickOutside) {
        this.hideOnClickAway(event, this.$refs.modal, this.close);
      }
      this.$bus.$emit('overlay-clicked');
    },
    open() {
      this.$bus.$emit('disable-scroll');
      this.isOpen = true;
      this.$nextTick(() => {
        this.showSlideOver = true;
      });
    },
    touchStart(event) {
      this.startY = event.touches[0].clientY;
    },
    touchEnd(event) {
      this.endY = event.changedTouches[0].clientY;
      if ((this.endY - this.startY > 150) && this.isSlidein) {
        this.close();
      }
    },
  },
};
</script>

<style
  lang="scss"
  scoped
>
:root:has(.slide-over-visible) {
  overflow: hidden;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transform: translateY(0);
  transition-duration: 400ms;
  transition-property: transform;
  transition-timing-function: ease-in-out;
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
}

@media (min-width: 640px) {
  .slide-up-enter-active,
  .slide-up-leave-active {
    transform: none;
    opacity: 1;
  }

  .slide-up-enter,
  .slide-up-leave-active {
    transform: none;
    opacity: 0;
  }
}
</style>
